<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_Menu' });
type MenuItem = {
	label?: string;
	icon?: string | [string, string];
	disabled?: boolean;
	/** Shown under the label while the item is disabled. */ reason?: string | null;
	visible?: boolean;
	separator?: boolean;
	command?: (event: { originalEvent: Event; item: MenuItem }) => void;
	items?: MenuItem[];
};
const props = withDefaults(defineProps<{ model?: MenuItem[]; popup?: boolean }>(), { model: () => [], popup: false });
const emit = defineEmits<{ show: []; hide: [] }>();
const attrs = useAttrs();
const root = ref<HTMLElement | null>(null);
const visible = ref(!props.popup);
const position = ref({ left: '0px', top: '0px' });
function normalizeIcon(icon?: string | [string, string]): string | [string, string] {
	if (Array.isArray(icon)) return icon;
	return icon?.replace(/^pi\s+pi-/, '') || 'circle-question';
}
function show(event: Event) {
	const element = event.currentTarget as HTMLElement | null;
	const rect = element?.getBoundingClientRect();
	position.value = { left: `${Math.max(8, Math.min(rect?.left ?? 8, window.innerWidth - 220))}px`, top: `${(rect?.bottom ?? 8) + 6}px` };
	visible.value = true;
	emit('show');
}
function hide() {
	if (!visible.value) return;
	visible.value = false;
	emit('hide');
}
function toggle(event: Event) {
	visible.value ? hide() : show(event);
}
function activate(item: MenuItem, event: Event) {
	if (item.disabled) return;
	item.command?.({ originalEvent: event, item });
	if (props.popup) hide();
}
function outside(event: MouseEvent) {
	if (props.popup && root.value && !root.value.contains(event.target as Node)) hide();
}
defineExpose({ show, hide, toggle });
onMounted(() => document.addEventListener('mousedown', outside));
onBeforeUnmount(() => document.removeEventListener('mousedown', outside));
</script>

<template>
	<Teleport v-if="popup" to="body">
		<div v-if="visible" ref="root" v-bind="attrs" :style="position" :class="['fixed z-[10000] min-w-48 overflow-hidden rounded-lg border border-surface bg-[var(--egs-overlay-background)] p-1 text-color shadow-dropdown', attrs['class']]">
			<ul class="m-0 list-none p-0">
				<template v-for="(item, index) in model" :key="index"
					><li v-if="item.separator" class="my-1 h-px bg-[var(--egs-content-border)]"></li>
					<li v-else-if="item.visible !== false">
						<button
							type="button"
							class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition hover:bg-[var(--egs-content-hover)] disabled:cursor-not-allowed disabled:opacity-50"
							:disabled="Boolean(item.disabled)"
							@click="activate(item, $event)"
						>
							<font-awesome-icon v-if="item.icon" :icon="normalizeIcon(item.icon)" class="w-4 text-color-secondary" />
							<span class="min-w-0">
								<span class="block">{{ item.label }}</span>
								<span v-if="item.disabled && item.reason" class="block text-[0.68rem] leading-snug text-color-secondary">{{ item.reason }}</span>
							</span>
						</button>
					</li></template
				>
			</ul>
		</div>
	</Teleport>
	<div v-else ref="root" v-bind="attrs" :class="['min-w-48 overflow-hidden rounded-lg border border-surface bg-[var(--egs-overlay-background)] p-1 text-color shadow-dropdown', attrs['class']]">
		<ul>
			<li v-for="(item, index) in model" :key="index">
				<button type="button" class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-[var(--egs-content-hover)]" @click="activate(item, $event)">{{ item.label }}</button>
			</li>
		</ul>
	</div>
</template>
